import { FeatureFlags } from '@dupe/common/constants'
import { getCookie, setCookie } from 'cookies-next'
import React, { createContext, useContext, useEffect, useState } from 'react'
import {
  isBrowser,
  isChrome,
  isDesktop,
  isMobile,
  isMobileSafari,
} from 'react-device-detect'
import { useAppContext } from '~/src/context/AppProvider'
import useExtensionState from '~/src/hooks/useExtensionState'
import useIsFeatureOn from '~/src/hooks/useIsFeatureOn'
import useIsSupportedCountries from '~/src/hooks/useIsSupportedCountries'

// TODO: move to constants
const SUPPORTED_COUNTRIES = ['us', 'ca']
const DUPE_EXTENSION_MODAL_HASHES_COOKIE_KEY = '__dupe_ext_modal_hashes'
const DUPE_EXTENSION_MODAL_SHOWN_COOKIE_KEY = '__dupe_ext_modal_shown'

interface IExtensionModalContext {
  loading: boolean
  featureOn: boolean | null
  installed?: boolean
  shouldBlockContent: boolean
  blockContentFeatureOn: boolean | null
  shouldShowModal: boolean
  modalAlreadyShown: boolean
  setModalShown: () => void
  showHellobar: boolean
  setShowHellobar: (show: boolean) => void
  safariMobile: boolean
}

const defaultContext = {
  loading: true,
  featureOn: false,
  installed: false,
  shouldBlockContent: false,
  blockContentFeatureOn: false,
  shouldShowModal: false,
  modalAlreadyShown: false,
  setModalShown: () => {},
  showHellobar: false,
  setShowHellobar: () => {},
  safariMobile: false,
}

// Create the context
const ExtensionModalContext =
  createContext<IExtensionModalContext>(defaultContext)

// Custom hook to access the context
export const useExtensionModalProvider = () => {
  const context = useContext(ExtensionModalContext)
  if (!context) {
    throw new Error(
      'useExtensionModalProvider must be used within a <ExtensionModalProvider> component'
    )
  }
  return context
}

const set24HourCookie = (key: string, value: any) => {
  const expires = new Date()
  expires.setTime(expires.getTime() + 24 * 60 * 60 * 1000) // 24 hours
  setCookie(key, value, {
    expires,
    path: '/',
  })
}

// Provider component
const ExtensionModalProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { hash } = useAppContext()
  const { loading, installed } = useExtensionState()
  const [showHellobar, setShowHellobar] = useState(false)
  const [total, setTotal] = useState(0)

  const { isInSupportedCountry, isLoading } =
    useIsSupportedCountries(SUPPORTED_COUNTRIES)

  const { featureOn, loading: loadingFeatureFlag } = useIsFeatureOn(
    FeatureFlags.DesktopExtensionWall
  )

  const { featureOn: blockContentFeatureOn } = useIsFeatureOn(
    FeatureFlags.DesktopExtensionBlockContent
  )

  const blockContentFeatureOnMobileSafari = false
  // const { featureOn: blockContentFeatureOnMobileSafari } = useIsFeatureOn(
  //   FeatureFlags.DesktopExtensionBlockContentMobileSafari
  // )

  // const isBrave = browserName === 'Brave'
  // const isEdge = browserName === 'Edge'

  const modalAlreadyShown = !!getCookie(DUPE_EXTENSION_MODAL_SHOWN_COOKIE_KEY)
  const shouldShowModal = !!featureOn && !modalAlreadyShown && total > 1

  const blockSupportedCountries = isInSupportedCountry && !isLoading

  const shouldBlockMobileSafari =
    isMobileSafari &&
    isMobile &&
    blockContentFeatureOnMobileSafari &&
    !loading &&
    !installed &&
    blockSupportedCountries // Ensure the user is in a supported country (iOS only supports US and CA at the moment)

  const shouldBlockContentDesktop =
    isBrowser &&
    isChrome &&
    !!blockContentFeatureOn &&
    !loading &&
    !installed &&
    isDesktop

  const shouldBlockContent =
    shouldBlockMobileSafari || shouldBlockContentDesktop

  const setModalShown = () => {
    set24HourCookie(DUPE_EXTENSION_MODAL_SHOWN_COOKIE_KEY, '1')
  }

  useEffect(() => {
    if (!hash) return
    if (loadingFeatureFlag) return
    if (!featureOn) return

    const existingCookie = getCookie(DUPE_EXTENSION_MODAL_HASHES_COOKIE_KEY)
    const existingCookieArray =
      (typeof existingCookie === 'string' ? existingCookie : '')?.split(',') ||
      []
    const value = Array.from(new Set([...existingCookieArray, hash]))
    const valueStr = value.join(',')

    setTotal(value.length)
    set24HourCookie(DUPE_EXTENSION_MODAL_HASHES_COOKIE_KEY, valueStr)
  }, [hash, featureOn, loadingFeatureFlag])

  return (
    <ExtensionModalContext.Provider
      value={{
        featureOn: !!featureOn,
        loading,
        installed,
        shouldBlockContent,
        blockContentFeatureOn: !!blockContentFeatureOn,
        shouldShowModal,
        modalAlreadyShown,
        setModalShown,
        showHellobar,
        setShowHellobar,
        safariMobile: shouldBlockMobileSafari || false,
      }}
    >
      {children}
    </ExtensionModalContext.Provider>
  )
}

export default ExtensionModalProvider
